import { cn } from '../../lib/utils'
import { Search } from 'lucide-react'
import type { GraphFilters } from './graphTypes'
import { ALL_NODE_TYPES, NODE_TYPE_LABELS } from './graphTypes'
import { NODE_THEME } from './graphTheme'

interface GraphControlsProps {
  filters: GraphFilters
  onChange: (filters: GraphFilters) => void
}

export function GraphControls({ filters, onChange }: GraphControlsProps) {
  const toggleType = (type: string) => {
    const next = new Set(filters.nodeTypes)
    if (next.has(type)) next.delete(type)
    else next.add(type)
    onChange({ ...filters, nodeTypes: next })
  }

  return (
    <div className={cn(
      'flex items-center gap-3 px-4 py-2',
      'bg-[var(--bg-base,var(--bg-base))] border-b border-white/[0.06]',
    )}>
      {/* Search */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-md',
        'bg-[var(--bg-elevated,var(--bg-elevated))] border border-white/[0.06]',
        'focus-within:border-[var(--accent,var(--accent))]/30',
      )}>
        <Search className="h-3 w-3 text-[var(--text-hint,#475569)]" />
        <input
          value={filters.searchQuery}
          onChange={e => onChange({ ...filters, searchQuery: e.target.value })}
          placeholder="Search nodes..."
          className={cn(
            'bg-transparent border-none outline-none text-[11px] w-36',
            'text-[var(--text-primary,#F0F2F5)] placeholder:text-[var(--text-hint,#475569)]',
          )}
        />
      </div>

      {/* Type toggles */}
      <div className="flex items-center gap-1">
        {ALL_NODE_TYPES.map(type => {
          const active = filters.nodeTypes.has(type)
          const th = NODE_THEME[type as keyof typeof NODE_THEME]
          return (
            <button
              key={type}
              onClick={() => toggleType(type)}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-all',
                active
                  ? 'bg-white/[0.06] text-[var(--text-secondary,#94A3B8)]'
                  : 'text-[var(--text-hint,#475569)] opacity-50',
              )}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: active ? th?.color : '#475569' }}
              />
              {NODE_TYPE_LABELS[type] || type}
            </button>
          )
        })}
      </div>
    </div>
  )
}
